// src/pages/healthcare/patient/modalComponents/submodals/UploadFileModal.tsx

import React, { useState } from "react";
import { Modal, Button, Upload, message, Progress } from "antd";
import { UploadOutlined, InboxOutlined } from "@ant-design/icons";
import { uploadFiles } from "../../../../../api/file/fileServiceApi";
import type { UploadFile } from "antd/es/upload/interface";
import type { UserUuid } from "../../../../../types/identity";

const { Dragger } = Upload;

interface UploadFileModalProps {
	patientId: UserUuid;
	parentId?: string;
	onClose: () => void;
	onFilesUploaded: () => void;
}

const UploadFileModal: React.FC<UploadFileModalProps> = ({ patientId, parentId, onClose, onFilesUploaded }) => {
	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [uploadProgress, setUploadProgress] = useState<number>(0);

	const handleFileChange = (info: any) => {
		setFileList(info.fileList);
	};

	const handleUpload = async () => {
		if (fileList.length === 0) {
			message.warning("Please select at least one file");
			return;
		}

		setIsUploading(true);
		setUploadProgress(0);

		try {
			const files = fileList.map((f) => f.originFileObj as File);

			// Simulate progress for better UX
			const progressInterval = setInterval(() => {
				setUploadProgress((prev) => {
					if (prev >= 90) {
						clearInterval(progressInterval);
						return 90;
					}
					return prev + 10;
				});
			}, 200);

			await uploadFiles(files, patientId, parentId);

			clearInterval(progressInterval);
			setUploadProgress(100);

			message.success(`${files.length} file(s) uploaded successfully`);
			onFilesUploaded();
		} catch (error) {
			console.error(error);
			message.error("Failed to upload files");
		} finally {
			setIsUploading(false);
			setUploadProgress(0);
		}
	};

	const uploadProps = {
		onRemove: (file: UploadFile) => {
			const index = fileList.indexOf(file);
			const newFileList = fileList.slice();
			newFileList.splice(index, 1);
			setFileList(newFileList);
		},
		beforeUpload: (file: File) => {
			setFileList((prev) => [
				...prev,
				{
					uid: file.name + Date.now(),
					name: file.name,
					status: "done",
					url: URL.createObjectURL(file),
					originFileObj: file,
				} as UploadFile,
			]);
			return false;
		},
		fileList,
		multiple: true,
	};

	return (
		<Modal
			title="Upload Files"
			open={true}
			onCancel={onClose}
			destroyOnClose
			footer={[
				<Button key="cancel" onClick={onClose} disabled={isUploading}>
					Cancel
				</Button>,
				<Button key="submit" type="primary" onClick={handleUpload} disabled={fileList.length === 0 || isUploading} loading={isUploading} icon={<UploadOutlined />}>
					{isUploading ? "Uploading..." : `Upload ${fileList.length} file(s)`}
				</Button>,
			]}
			width={600}
		>
			<Dragger {...uploadProps} style={{ marginTop: 16 }}>
				<p className="ant-upload-drag-icon">
					<InboxOutlined />
				</p>
				<p className="ant-upload-text">Click or drag files to this area to upload</p>
				<p className="ant-upload-hint">Support for multiple file uploads. Select all files you want to upload at once.</p>
			</Dragger>

			{isUploading && <Progress percent={uploadProgress} status="active" style={{ marginTop: 16 }} />}
		</Modal>
	);
};

export default UploadFileModal;
